import React, { useState, useEffect } from 'react';
import { Home, Lightbulb, Plug, Smartphone, Shield, Zap, Clock, Sun, Moon, CheckCircle } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import QuoteForm from '../components/QuoteForm';
import CardCarousel, { CarouselCard } from '../components/ui/CardCarousel';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const AutomationPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API}/products?category=automation`);
        if (response.ok) {
          const data = await response.json();
          setProducts(data);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const features = [
    { 
      icon: Lightbulb, 
      title: 'Éclairage intelligent', 
      desc: 'Contrôlez toutes vos lumières depuis l\'app Ajax. Interrupteurs tactiles LightSwitch avec design épuré et rétroéclairage LED personnalisable.',
      color: 'yellow'
    },
    { 
      icon: Plug, 
      title: 'Prises connectées', 
      desc: 'Gérez vos appareils électriques à distance. Mesure de consommation en temps réel, programmation horaire et arrêt automatique.',
      color: 'blue'
    },
    { 
      icon: Home, 
      title: 'Scénarios intelligents', 
      desc: 'Créez des automatisations personnalisées : mode "Départ", "Nuit", "Vacances"... Un bouton pour tout contrôler.',
      color: 'purple'
    },
    { 
      icon: Smartphone, 
      title: 'App Ajax unifiée', 
      desc: 'Sécurité et domotique dans une seule application. Contrôle complet depuis votre smartphone, où que vous soyez.',
      color: 'green'
    },
  ];

  const scenarios = [
    { 
      name: 'Mode Départ', 
      icon: Sun,
      actions: ['Éteindre toutes les lumières', 'Armer l\'alarme', 'Simuler une présence'],
      trigger: 'Un appui sur SpaceControl'
    },
    { 
      name: 'Mode Nuit', 
      icon: Moon,
      actions: ['Lumière tamisée salon', 'Éteindre rez-de-chaussée', 'Armer périmétrique'],
      trigger: 'Double appui SideButton'
    },
    { 
      name: 'Mode Vacances', 
      icon: Zap,
      actions: ['Simulation de présence', 'Alertes mouvement', 'Contrôle chauffage'],
      trigger: 'Depuis l\'application'
    },
  ];

  const integrationBenefits = [
    { text: 'Éclairage qui s\'allume en cas d\'alarme intrusion' },
    { text: 'Volets qui se ferment automatiquement la nuit' },
    { text: 'Chauffage qui s\'éteint quand vous partez' },
    { text: 'Portail qui s\'ouvre à votre approche' },
    { text: 'Simulation de présence intelligente en vacances' },
    { text: 'Notification si un appareil consomme anormalement' },
  ];

  return (
    <div className="min-h-screen py-12 bg-black" data-testid="automation-page">
      {/* Hero Header */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="relative py-16 md:py-20 rounded-2xl md:rounded-3xl overflow-hidden bg-[#0a0a0a] border border-white/5">
          <div className="relative text-center px-4">
            <p className="text-emerald-400 text-sm font-medium tracking-wide uppercase mb-4">Confort & Automatisation</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-6 tracking-tight">
              Domotique <span className="text-emerald-400">Ajax</span>
            </h1>
            <p className="text-lg text-white/80 max-w-3xl mx-auto mb-8">
              Transformez votre maison en habitat intelligent. 
              Contrôlez éclairages, prises et appareils depuis votre smartphone. 
              Intégration native avec votre système de sécurité Ajax.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-fgs-primary/40 rounded-lg">
                <Shield className="w-5 h-5 text-emerald-400" />
                <span className="text-white font-medium">Garantie 4 ans FGS</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 rounded-lg">
                <Clock className="w-5 h-5 text-emerald-400" />
                <span className="text-white font-medium">Installation sur devis</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features - Carousel */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-20">
        <CardCarousel
          title="Automatisez votre quotidien"
          subtitle="Confort & Domotique"
          showDots={true}
        >
          {features.map((feature, i) => (
            <CarouselCard
              key={i}
              icon={feature.icon}
              title={feature.title}
              description={feature.desc}
              color={`bg-${feature.color}-500/20`}
            />
          ))}
        </CardCarousel>
      </section>

      {/* Scenarios Section - Carousel */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-20">
        <div className="bg-gradient-to-r from-emerald-900/30 to-fgs-surface border border-emerald-500/20 rounded-2xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-8 h-8 text-emerald-400" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase">
              Scénarios intelligents
            </h2>
          </div>
          <p className="text-white/80 mb-8 max-w-3xl">
            Créez des automatisations qui simplifient votre vie. Un seul geste déclenche 
            plusieurs actions simultanément. Personnalisez vos scénarios selon vos habitudes.
          </p>
          <CardCarousel showDots={true} showArrows={false}>
            {scenarios.map((scenario, i) => (
              <div key={i} className="bg-black/40/50 rounded-xl p-6 border border-emerald-500/10 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <scenario.icon className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white">{scenario.name}</h3>
                </div>
                <ul className="space-y-2 mb-4">
                  {scenario.actions.map((action, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-white/50">
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      {action}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-emerald-400 font-medium">{scenario.trigger}</p>
              </div>
            ))}
          </CardCarousel>
        </div>
      </section>

      {/* Integration Benefits - Carousel */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-20">
        <CardCarousel
          title="Sécurité + Domotique = Maison intelligente"
          subtitle="Synergie Ajax"
          showDots={true}
        >
          {integrationBenefits.map((benefit, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl p-5 flex items-center gap-4 hover:border-emerald-500/40 transition-colors h-full min-h-[80px]">
              <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <span className="text-white text-sm leading-relaxed">{benefit.text}</span>
            </div>
          ))}
        </CardCarousel>
      </section>

      {/* Products and Quote */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Products */}
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-semibold text-white uppercase mb-4">
              Produits domotique
            </h2>
            <p className="text-white/50 mb-8">
              Interrupteurs intelligents, relais, prises connectées et boutons de commande. 
              Nos experts dimensionneront votre installation selon vos besoins.
            </p>
            
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl h-80 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.product_id} product={product} showQuote />
                ))}
              </div>
            )}
          </div>

          {/* Quote Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-28">
              <QuoteForm category="automation" products={products} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationPage;
